import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { formatRef, isValidSlug, slugify } from "@strat/format";

import { api } from "../api";
import { writeClipboard } from "./CopySettings";

/** Where a reference opens: `/s/alex/rsi-dip:v2`. */
export function refPath(namespace: string, slug: string, version?: number | null): string {
  return `/s/${namespace.toLowerCase()}/${slug}${version ? `:v${version}` : ""}`;
}

/**
 * A strategy reference, `alex/rsi-dip:v2@sha256:5dc8eb7c3130`, in the shape of a
 * container image name: who, which strategy, which version, and the hash of the
 * exact rules. The short digest is shown; Copy copies the full reference.
 */
export function RefChip({
  namespace,
  slug,
  version,
  digest,
  link = true,
  compact = false,
}: {
  namespace: string;
  slug: string;
  version?: number | null;
  digest?: string | null;
  link?: boolean;
  /** Name and tag only, for cards and tables. */
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const full = formatRef({ namespace, slug, version, digest: digest ?? undefined });
  const name = formatRef({ namespace, slug, version }, 0);
  const label = (
    <>
      <span className="refchip__name">{name}</span>
      {!compact && digest && <span className="refchip__digest">@sha256:{digest.slice(0, 12)}</span>}
    </>
  );
  return (
    <span className="refchip" title={full}>
      {link ? (
        <Link className="refchip__ref" to={refPath(namespace, slug, version)}>
          {label}
        </Link>
      ) : (
        <span className="refchip__ref">{label}</span>
      )}
      {!compact && (
        <button
          type="button"
          className="refchip__copy"
          aria-label={`Copy the reference ${full}`}
          onClick={() => {
            void writeClipboard(full).then(() => setCopied(true));
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? "✓" : "⧉"}
        </button>
      )}
    </span>
  );
}

/** The owner's control for the name in a reference. The old name keeps working. */
export function SlugEditor({ strategyId, slug, name }: { strategyId: string; slug: string; name: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(slug);
  const save = useMutation({
    mutationFn: (next: string) =>
      api<{ slug: string }>(`/hub/strategies/${strategyId}/slug`, { method: "PATCH", body: { slug: next } }),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["hub-strategy", strategyId] });
    },
  });
  if (!open)
    return (
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        onClick={() => {
          setValue(slug);
          setOpen(true);
        }}
        title="Change the name used in this strategy's reference"
      >
        ✎ Rename reference
      </button>
    );
  const valid = isValidSlug(value);
  return (
    <form
      className="row slug-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate(value);
      }}
    >
      <input
        value={value}
        onChange={(e) => setValue(e.target.value.toLowerCase())}
        aria-label="Name in the reference"
        aria-invalid={!valid}
        maxLength={48}
        className="mono"
      />
      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setValue(slugify(name))}>
        From name
      </button>
      <button type="submit" className="btn btn--sm btn--primary" disabled={!valid || save.isPending}>
        Save
      </button>
      <button type="button" className="btn btn--sm" onClick={() => setOpen(false)}>
        Cancel
      </button>
      <span className="muted small">
        {save.error
          ? save.error.message
          : valid
            ? "Links with the old name keep working."
            : "2–48 lower-case letters, digits and hyphens."}
      </span>
    </form>
  );
}
